import axios from "axios";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { getTemplate } from "../components/testing/Templates";

const TERMINAL_CHECK_STATUSES = ["passed", "failed", "skipped", "aborted"];

function mergeCheckRow(prevRow, response) {
  const prevStatus = prevRow.status;
  const nextStatus = response.status ?? prevStatus;
  const status =
    TERMINAL_CHECK_STATUSES.includes(prevStatus) &&
    (nextStatus === "running" || nextStatus === "waiting")
      ? prevStatus
      : nextStatus;

  const becomingRunning =
    status === "running" && prevStatus !== "running";

  const durationAnchorMs =
    response.durationAnchorMs !== undefined
      ? response.durationAnchorMs
      : becomingRunning
        ? Date.now()
        : prevRow.durationAnchorMs;

  const durationFrozen =
    response.durationFrozen !== undefined
      ? response.durationFrozen
      : status === "running"
        ? false
        : prevRow.durationFrozen;

  return {
    ...prevRow,
    ...response,
    name: response.name || prevRow.name,
    comments:
      response.comments !== undefined && response.comments !== null
        ? response.comments
        : prevRow.comments,
    status,
    result: response.result !== undefined ? response.result : prevRow.result,
    durationFrozen,
    durationAnchorMs,
    runningSinceMs:
      response.runningSinceMs !== undefined
        ? response.runningSinceMs
        : becomingRunning
          ? Date.now()
          : status === "running"
            ? prevRow.runningSinceMs || durationAnchorMs
            : null,
    durationFormatted:
      response.durationFormatted !== undefined
        ? response.durationFormatted
        : prevRow.durationFormatted,
  };
}

const scenarios = {
  default_on: [],
  emergency: [
    "pr1_check",
    "pr2_check",
    "fs1_check",
    "fs2_check",
    "fs3_check",
    "tds1_check",
    "tds2_check",
    "tr_check",
    "leakage_check",
  ],
  master: ["sed_flush", "carb_flush", "ro_flush", "alk_flush"],
  check_request: [
    "sed_flush",
    "carb_flush",
    "ro_flush",
    "alk_flush",
    "all_flush",
  ],
  reset_wifi: [],
  termination: [
    "sed_flush",
    "carb_flush",
    "ro_flush",
    "alk_flush",
    "master_program",
  ],
  dnd: [],
  reboot: [],
  pulse_calibration: ["fs1", "fs2", "fs3"],
};

export const UserContext = createContext({
  isTesting: false,
  scenarios: scenarios,
  deviceId: "F02880CD31E8",
  setDeviceId: () => { },
  deviceIds: [],
  inventoryDeviceIds: [],
  scenario: "default_on",
  setScenario: () => { },
  subscenario: null,
  setSubscenario: () => { },
  login: () => { },
  logout: () => { },
  loggedIn: false,
  token: null,
  flushDuration: -1,
  setFlushDuration: () => { },
  flushType: "check",
  setFlushType: () => { },
  checksData: {},
  csvData: {},
  abortData: null,
  checkingProgress: true,
  startTesting: () => { },
  stopTesting: () => { },
  manualConfirm: () => { },
  fixMinMax: () => { },
});

export const useUserContext = () => {
  return useContext(UserContext);
};

export default function UserContextProvider({ children }) {
  const [isTesting, setIsTesting] = useState(false);
  const [loggedIn, setLoggedIn] = useState(() => !!(localStorage.getItem("token") && localStorage.getItem("phone")));
  const [loading, setLoading] = useState(false);
  const [deviceId, setDeviceId] = useState("F02880CD31E8");
  const [deviceIds, setDeviceIds] = useState([]);
  const [inventoryDeviceIds, setInventoryDeviceIds] = useState([]);
  const [token, setToken] = useState(() => localStorage.getItem("token") || undefined);
  const [scenario, setScenario] = useState("default_on");
  const [subscenario, setSubscenario] = useState(scenarios["default_on"][0]);
  const [flushDuration, setFlushDuration] = useState(-1);
  const [flushType, setFlushType] = useState("check");
  const [deviceCapabilities, setDeviceCapabilities] = useState(null);
  const [checksData, setChecksData] = useState({});
  const [csvData, setCsvData] = useState({});
  const [abortData, setAbortData] = useState(null);
  const [finalized, setFinalized] = useState(false);
  const [checkingProgress, setCheckingProgress] = useState(false);
  const [socket, setSocket] = useState();
  const location = useLocation();
  const navigate = useNavigate();
  const [mainPcbDevice, setMainPcbDevice] = useState();
  const [testPcbDevice, setTestPcbDevice] = useState();
  const [testScenario, setTestScenario] = useState();
  const [isPcbTesting, setIsPcbTesting] = useState(false);
  const [sensorData, setSensorData] = useState();
  const [subscenarioTestPcb, setSubscenarioTestPcb] = useState();
  const intentionalDisconnectRef = useRef(false);
  const startInFlightRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/deviceconfig/get-all-devices`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        setDeviceIds(res.data.map((device) => device.device_id));
      } catch (e) {
        console.error(e);
      }
    })();
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/inventory/get-all-products`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.status !== 200) throw new Error(res.data.message);
        setInventoryDeviceIds(
          res.data.data.data.map((device) => device.device_id),
        );
      } catch (e) {
        console.error(e);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!deviceId || deviceId.trim().length < 8) {
      setDeviceCapabilities(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_SCRIPT_API_URL}/testing/device-capabilities`,
          { deviceId },
        );
        if (cancelled) return;
        if (res.status === 200) {
          setDeviceCapabilities(res.data);
        }
      } catch (e) {
        console.error("Failed to load device capabilities", e);
        if (!cancelled) setDeviceCapabilities(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  useEffect(() => {
    // Double-check localStorage to avoid redirecting during first-render hydration
    const hasStoredToken = !!(localStorage.getItem("token") && localStorage.getItem("phone"));
    if (!loading && location.pathname !== "/login" && !loggedIn && !hasStoredToken) {
      navigate(`/login?to=${location.pathname}`);
    }
  }, [location.pathname, loading, loggedIn, navigate]);

  async function login(token, role, phone) {
    localStorage.setItem("token", token);
    localStorage.setItem("role", role);
    localStorage.setItem("phone", phone);
    setLoggedIn(true);
    setToken(token);
  }

  function logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("phone");
    setLoggedIn(false);
    setToken(undefined);
    disconnectSocket();
    navigate("/login");
  }

  function disconnectSocket() {
    if (socket) {
      socket.off("message", onMessage);
      socket.off("connect");
      socket.off("disconnect");
      socket.off("error");
      if (socket.connected) {
        intentionalDisconnectRef.current = true;
        socket.disconnect();
      }
      setSocket(undefined);
    }
  }

  function handleSocketDisconnect(reason) {
    if (!intentionalDisconnectRef.current) {
      toast.error(reason);
    }
    intentionalDisconnectRef.current = false;
  }

  function endTestingSession() {
    startInFlightRef.current = false;
    setIsTesting(false);
    setCheckingProgress(false);
    disconnectSocket();
  }

  function onMessage(response) {
    try {
      if (typeof response === "string") response = JSON.parse(response);
      if (response && response.type === "toast") {
        const msg = response.message || "Notification";
        if (response.toastType === "error") toast.error(msg);
        else if (response.toastType === "success") toast.success(msg);
        else toast(msg);
        return;
      }
      if (response && response.type === "test-error") {
        toast.error(response.error || "Test failed");
        setFinalized(true);
        endTestingSession();
        return;
      }
      if (response && response.name) {
        if (response.name === "abort") {
          setAbortData(response);
          setFinalized(true);
          setChecksData((prev) => {
            Object.keys(prev).forEach((key) => {
              if (!["passed", "failed", "aborted", "skipped"].includes(prev[key].status))
                prev[key].status = "aborted";
            });
            return { ...prev };
          });
          endTestingSession();
          return;
        } else if (response.name === "completed") {
          setFinalized(true);
          setChecksData((prev) => {
            const prevRow = prev.completed || {};
            const updated = {
              ...prev,
              completed: {
                ...prevRow,
                ...response,
                name: response.name || prevRow.name || "return to default on",
                comments: response.comments ?? prevRow.comments,
              },
            };
            Object.keys(updated).forEach((key) => {
              if (key === "completed") return;
              // Keep manual Select rows selectable after the automated run finishes
              if (updated[key]?.input) return;
              if (updated[key].status === "waiting" || updated[key].status === "running") {
                updated[key].status = "skipped";
              }
            });
            return updated;
          });
          if (response.result === "success") {
            toast.success("Test completed successfully");
          } else {
            toast.error(response.comments || "Test failed");
          }
          // Keep websocket alive so notification received Yes/No can still be saved
          setIsTesting(false);
          setCheckingProgress(false);
          startInFlightRef.current = false;
          return;
        } else {
          // Ignore unknown / legacy ghost rows (e.g. "notification triggered received In-App")
          const isGhost =
            /notification triggered received/i.test(response.name) ||
            /triggered received/i.test(response.name);
          if (isGhost) return;

          setChecksData((prev) => {
            if (!prev[response.name] && response.type === "notification-status") {
              return prev;
            }
            // Only create rows that already exist in the template for this run
            if (!prev[response.name]) return prev;
            return {
              ...prev,
              [response.name]: mergeCheckRow(prev[response.name] || {}, response),
            };
          });
          setCsvData((prev) => {
            if (/notification triggered received/i.test(response.name)) return prev;
            return {
              ...prev,
              [response.name]: prev[response.name]
                ? [...prev[response.name], response]
                : [response],
            };
          });
        }
      } else if (response && response.type === "update") {
        if (response.update === "reconnect") {
          setScenario(response.scenario);
          setSubscenario(response.subscenario);
          let csv = {};
          // data = {check, status, last_run, duration, comments, result, timestamp}
          response.checksData.forEach((c) => {
            if (!csv[c.check]) csv[c.check] = [];
            csv[c.check].push({
              name: c.check,
              status: c.status,
              lastRun: c.last_run,
              duration: c.duration,
              comments: c.comments,
              result: c.result,
            });
          });

          let cd = {};
          const template = getTemplate(response.scenario, response.subscenario, {
            supportedFlushes: deviceCapabilities?.supportedFlushes,
            hasAudio: deviceCapabilities?.hasAudio,
          });
          Object.keys(csv).forEach((c) => {
            cd[c] = csv[c][csv[c].length - 1];
            delete template[c];
          });
          cd = { ...cd, ...template };
          setChecksData(cd);
          setCsvData(csv);
          toast.success(
            "Testing on the device is already running. Reconnected to the session",
          );
        }
      }
    } catch (err) {
      toast(response);
    }
  }

  async function startTesting(
    deviceId,
    scenario,
    subscenario,
    datasetLocation,
  ) {
    if (isTesting || checkingProgress || startInFlightRef.current) {
      toast.error("A test is already running — stop it first");
      return;
    }
    startInFlightRef.current = true;

    const sock = io(process.env.REACT_APP_WS_URL);
    try {
      await new Promise((resolve, reject) => {
        sock.on("connect", () => {
          toast.success("Session connected to the backend");
          resolve();
        });
        sock.on("connect_error", (err) => {
          toast.error(err.message);
          reject(err.message);
        });
      });
      sock.on("error", (err) => {
        toast.error(err);
      });
      sock.on("disconnect", handleSocketDisconnect);
      sock.on("message", (data) => {
        onMessage(data);
      });
    } catch (err) {
      toast.error("Session not connected to the backend");
      startInFlightRef.current = false;
      return;
    }
    setSocket(sock);
    if (!token || !sock || !sock.connected) {
      toast.error("Session not connected to the backend");
      startInFlightRef.current = false;
      return;
    }
    setDeviceId(deviceId);
    setScenario(scenario);
    setSubscenario(subscenario);
    setChecksData(
      getTemplate(scenario, subscenario, {
        supportedFlushes: deviceCapabilities?.supportedFlushes,
        hasAudio: deviceCapabilities?.hasAudio,
      }),
    );
    setCheckingProgress(true);
    setCsvData({});
    setAbortData();
    setFinalized(false);

    sock.send(
      JSON.stringify({
        type: "start-testing",
        deviceId,
        scenario,
        subscenario,
        datasetLocation,
        flushDuration,
        flushType,
        token,
      }),
    );
    setIsTesting(true);
  }

  async function startPcbTesting(mainPcbDevice, testPcbDevice, scenario) {
    const sock = io(process.env.REACT_APP_WS_URL);
    try {
      await new Promise((resolve, reject) => {
        sock.on("connect", () => {
          toast.success("Session connected to the backend");
          resolve();
        });
        sock.on("connect_error", (err) => {
          toast.error(err.message);
          reject(err.message);
        });
      });
      sock.on("error", (err) => {
        toast.error(err);
      });
      sock.on("disconnect", handleSocketDisconnect);
      sock.on("message", (data) => {
        onMessage(data);
      });
    } catch (err) {
      toast.error("Session not connected to the backend");
      return;
    }
    setSocket(sock);
    if (!token || !sock || !sock.connected) {
      toast.error("Session not connected to the backend");
      return;
    }

    let scenarioData = require("../components/pcb-testing/dependency_scenarios.json");
    let sensorData = scenarioData.find((s) => s.id === scenario).sensors;
    if (!sensorData) {
      toast.error("sensorData not found");
      return;
    }
    let scenarioName = scenarioData.find((s) => s.id === scenario).scenario;
    setMainPcbDevice(mainPcbDevice);
    setTestPcbDevice(testPcbDevice);
    setTestScenario(scenarioName);
    setSensorData(sensorData);
    const subscenario = scenarioData.find((s) => s.id === scenario)?.subscenario;
    setSubscenarioTestPcb(subscenario);
    console.log("sensorData", sensorData);
    console.log("subscenarioTestPcb", subscenario);

    sock.send(
      JSON.stringify({
        type: "start-pcb-testing",
        mainPcbDevice,
        testPcbDevice,
        scenarioName,
        token,
        sensorData,
        subScenario: subscenario,
      }),
    );
    setIsPcbTesting(true);
  }

  function stopPcbTesting() {
    if (!token || !socket || !socket.connected) {
      toast.error("Session not connected to the backend");
      return;
    }
    socket.send(
      JSON.stringify({
        testPcbDevice: testPcbDevice,
        type: "stop-pcb-testing",
        token,
      }),
    );
    setIsPcbTesting(false);
    setCheckingProgress(false);
    disconnectSocket();
  }

  function stopTesting() {
    if (!token || !socket || !socket.connected) {
      toast.error("Session not connected to the backend");
      return;
    }
    socket.send(
      JSON.stringify({
        deviceId: deviceId,
        type: "stop-testing",
        token,
      }),
    );
    startInFlightRef.current = false;
    setIsTesting(false);
    setCheckingProgress(false);
    disconnectSocket();
  }

  function manualConfirm(name) {
    if (!socket) {
      toast.error("Websocket not connected. Refresh the page and try again");
      return;
    }
    const row = checksData[name];
    if (!row) return;

    confirmAlert({
      title: (row.comments ?? name) + " ?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            applyManualConfirm(name, true);
            try {
              socket.send(
                JSON.stringify({
                  deviceId: deviceId,
                  type: "update",
                  scenario: scenario,
                  action: name,
                  value: true,
                }),
              );
            } catch (e) {
              console.error(e);
            }
          },
        },
        {
          label: "No",
          onClick: () => {
            let issue;
            if (name.match(/.*wifi.*/i))
              issue = prompt("Did you find any issue ?");
            applyManualConfirm(name, false, issue);
            try {
              socket.send(
                JSON.stringify({
                  deviceId: deviceId,
                  type: "update",
                  scenario: scenario,
                  action: name,
                  value: false,
                  comments: issue || "",
                }),
              );
            } catch (e) {
              console.error(e);
            }
          },
        },
      ],
      overlayClassName: "overlay",
    });
  }

  function applyManualConfirm(name, value, extraComments = "") {
    const answer = value ? "Yes" : "No";
    setChecksData((prev) => {
      const prevRow = prev[name] || { name };
      return {
        ...prev,
        [name]: {
          ...prevRow,
          name: prevRow.name || name,
          status: value ? "passed" : "failed",
          result: value ? "success" : "failed",
          comments: [
            prevRow.comments,
            `${name} : ${answer}`,
            extraComments || null,
          ]
            .filter(Boolean)
            .join("\n"),
          lastRun: new Date().toISOString(),
          duration: 0,
          durationFormatted: "0s",
          durationFrozen: true,
          input: prevRow.input
            ? { ...prevRow.input, clicked: true }
            : undefined,
        },
      };
    });
  }

  function fixMinMax() {
    if (!socket) {
      toast.error("Websocket not connected. Refresh the page and try again");
      return;
    }
    socket.send(
      JSON.stringify({
        deviceId: deviceId,
        type: scenario === "master" ? "fix-master" : "fix-emergency",
        scenario,
        subscenario,
      }),
    );
    toast.success("Fixing min-max values...");
  }

  useEffect(() => {
    if (finalized || abortData) {
      setCheckingProgress(false);
      return;
    }
    const terminal = ["passed", "failed", "aborted", "skipped"];
    const stillRunning = Object.values(checksData).some(
      (c) => !terminal.includes(c.status),
    );
    setCheckingProgress(stillRunning);
  }, [checksData, abortData, finalized]);

  return (
    <UserContext.Provider
      value={{
        testScenario,
        socket,
        scenarios,
        isTesting,
        deviceId,
        setDeviceId,
        deviceIds,
        inventoryDeviceIds,
        scenario,
        setScenario,
        subscenario,
        setSubscenario,
        login,
        logout,
        loggedIn,
        token,
        flushDuration,
        setFlushDuration,
        flushType,
        setFlushType,
        deviceCapabilities,
        checksData,
        csvData,
        abortData,
        checkingProgress,
        startTesting,
        stopTesting,
        manualConfirm,
        fixMinMax,
        isPcbTesting,
        startPcbTesting,
        stopPcbTesting,
        mainPcbDevice,
        testPcbDevice,
        sensorData,
        subscenarioTestPcb,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
